import React, { useState, useEffect } from 'react';

const GoogleReviewsWidget = ({ variant = 'full', maxReviews = 5 }) => {
  const [reviewsData, setReviewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/google`);
        if (!response.ok) {
          throw new Error('Failed to fetch reviews');
        }
        const data = await response.json();
        setReviewsData(data);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const renderStars = (rating) => {
    return (
      <div className="flex text-yellow-400">
        {[...Array(5)].map((_, i) => (
          <span key={i} className={i < rating ? 'text-yellow-400' : 'text-gray-300'}>
            ★
          </span>
        ))}
      </div>
    );
  };

  const truncateText = (text, maxLength = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Compact variant for homepage
  if (variant === 'compact') {
    if (loading) {
      return (
        <div className="bg-white rounded-lg shadow-lg p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto"></div>
        </div>
      );
    }

    if (error || !reviewsData) {
      return null; // Silently fail on compact variant
    }

    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-2">
            <img 
              src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
              alt="Google" 
              className="h-6 mr-2"
            />
            <span className="text-gray-600 font-medium">Reviews</span>
          </div>
          <div className="flex items-center justify-center space-x-2 mb-2">
            {renderStars(Math.round(reviewsData.rating))}
            <span className="text-2xl font-bold text-gray-800">{reviewsData.rating}</span>
          </div>
          <p className="text-gray-600">{reviewsData.total_reviews}+ Reviews</p>
        </div>

        {/* Show first 3 reviews in compact (only 4.9★+ reviews) */}
        <div className="space-y-4">
          {reviewsData.reviews.filter((r) => r.rating >= 4.9).slice(0, 3).map((review, index) => (
            <div key={index} className="border-b border-gray-100 pb-4 last:border-0">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-800">{review.author_name}</span>
                <span className="text-sm text-gray-500">{review.relative_time_description}</span>
              </div>
              <div className="flex mb-2">
                {renderStars(review.rating)}
              </div>
              <p className="text-gray-600 text-sm">{truncateText(review.text, 150)}</p>
            </div>
          ))}
        </div>

        <a
          href="https://g.page/r/CUolB0PBcru6EBM/review"
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-6 text-center text-teal-600 hover:text-teal-700 font-semibold"
        >
          Read All Reviews on Google →
        </a>
      </div>
    );
  }

  // Full variant for testimonials page
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/6 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600">Unable to load reviews. Please try again later.</p>
        <a
          href="https://g.page/r/CUolB0PBcru6EBM/review"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-4 text-teal-600 hover:text-teal-700 font-semibold"
        >
          View Reviews on Google →
        </a>
      </div>
    );
  }

  if (!reviewsData) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header with overall rating */}
      <div className="bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="flex items-center justify-center mb-4">
          <img 
            src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
            alt="Google" 
            className="h-8 mr-3"
          />
          <span className="text-xl text-gray-600 font-medium">Reviews</span>
        </div>
        
        <div className="flex items-center justify-center space-x-3 mb-2">
          <span className="text-5xl font-bold text-gray-800">{reviewsData.rating}</span>
          <div className="text-3xl">
            {renderStars(Math.round(reviewsData.rating))}
          </div>
        </div>
        
        <p className="text-gray-600 text-lg mb-4">
          Based on {reviewsData.total_reviews}+ reviews
        </p>
        
        <div className="flex justify-center space-x-4">
          <a
            href="https://g.page/r/CUolB0PBcru6EBM/review"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition"
          >
            View All Google Reviews
          </a>
          <a
            href="https://g.page/r/CUolB0PBcru6EBM/review"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white border-2 border-teal-600 text-teal-600 px-6 py-3 rounded-lg font-semibold hover:bg-teal-50 transition"
          >
            Leave a Review
          </a>
        </div>
      </div>

      {/* Individual Reviews */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {reviewsData.reviews.filter((r) => r.rating >= 4.9).slice(0, maxReviews).map((review, index) => (
          <div 
            key={index} 
            className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300"
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                {review.profile_photo_url ? (
                  <img 
                    src={review.profile_photo_url} 
                    alt={review.author_name}
                    className="w-12 h-12 rounded-full"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {review.author_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-bold text-gray-800 truncate">{review.author_name}</h4>
                  <span className="text-sm text-gray-500 flex-shrink-0 ml-2">
                    {review.relative_time_description}
                  </span>
                </div>
                <div className="mb-3">
                  {renderStars(review.rating)}
                </div>
                <p className="text-gray-700 leading-relaxed">
                  "{review.text}"
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer CTA */}
      <div className="text-center py-6">
        <p className="text-gray-600 mb-4">
          Thank you to all our wonderful patients for their kind words!
        </p>
        <a
          href="https://g.page/r/CUolB0PBcru6EBM/review"
          target="_blank"
          rel="noopener noreferrer"
          className="text-teal-600 hover:text-teal-700 font-semibold"
        >
          See more reviews on Google →
        </a>
      </div>
    </div>
  );
};

export default GoogleReviewsWidget;
